import { useState } from 'react';

import {
  Anchor,
  Badge,
  Button,
  Card,
  Collapse,
  Divider,
  Group,
  Select,
  Stack,
  TagsInput,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconEdit } from '@tabler/icons-react';

import { isVesselSubjectType, jurisdictionFieldLabel, SUBJECT_TYPE_OPTIONS } from '@/constants';
import {
  AdditionalInfo,
  Confidence,
  SubjectCandidateResponse,
  SubjectOverrides,
} from '@/types/subject-candidate';
import classes from './CandidateRow.module.css';

const COLLAPSE_SOURCES_OVER = 3;

interface CandidateRowProps {
  candidate: SubjectCandidateResponse;
  subjectName: string;
  subjectType?: string | null;
  reference?: string | null;
  jurisdictionCodes?: string[] | null;
  additionalInfo?: Record<string, unknown> | null;
  disabled?: boolean;
  onSelect: (candidateId: string, overrides?: SubjectOverrides) => void;
}

function confidenceColor(c: Confidence): string {
  switch (c) {
    case 'high':
      return 'green';
    case 'medium':
      return 'yellow';
    case 'low':
      return 'gray';
  }
}

function prettifyKey(key: string): string {
  return key.replaceAll('_', ' ').replaceAll(/\b\w/g, (ch) => ch.toUpperCase());
}

/** Stringify any value safely, avoiding `[object Object]` for non-primitives. */
function safeToString(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

function renderAttributeValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (Array.isArray(value)) {
    return value.map(safeToString).join(', ');
  }
  return safeToString(value);
}

function arraysEqual(a: string[] | null | undefined, b: string[] | null | undefined): boolean {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) {
    return false;
  }
  return left.every((v, i) => v === right[i]);
}

function stringToArrayValue(v: unknown): string[] {
  if (Array.isArray(v)) {
    return v.map(safeToString);
  }
  if (v === null || v === undefined || v === '') {
    return [];
  }
  return [safeToString(v)];
}

interface OverrideFormState {
  subject_name: string;
  subject_type: string;
  reference: string;
  jurisdiction_codes: string[];
  // additional_info
  date_of_birth: string;
  registration_number: string;
  imo_number: string;
  nationality: string;
  aliases: string[];
  addresses: string[];
  urls: string[];
  employer: string;
  previous_employers: string[];
  previous_locations: string[];
  spouse: string;
  family_members: string[];
}

function buildInitialForm(
  subjectName: string,
  subjectType: string | null | undefined,
  reference: string | null | undefined,
  jurisdictionCodes: string[] | null | undefined,
  additionalInfo: Record<string, unknown> | null | undefined,
  candidateAttributes: Record<string, unknown>
): OverrideFormState {
  const ai = (additionalInfo ?? {}) as Partial<AdditionalInfo> & Record<string, unknown>;
  return {
    subject_name: subjectName,
    subject_type: subjectType ?? '',
    reference: reference ?? '',
    jurisdiction_codes: jurisdictionCodes ?? [],
    date_of_birth:
      (ai.date_of_birth as string | null) ?? safeToString(candidateAttributes.date_of_birth),
    registration_number: (ai.registration_number as string | null) ?? '',
    imo_number: (ai.imo_number as string | null) ?? safeToString(candidateAttributes.imo_number),
    nationality: (ai.nationality as string | null) ?? safeToString(candidateAttributes.nationality),
    aliases: stringToArrayValue(ai.aliases),
    addresses: stringToArrayValue(ai.addresses),
    urls: stringToArrayValue(ai.urls),
    employer: (ai.employer as string | null) ?? safeToString(candidateAttributes.employer),
    previous_employers: stringToArrayValue(ai.previous_employers),
    previous_locations: stringToArrayValue(ai.previous_locations),
    spouse: (ai.spouse as string | null) ?? '',
    family_members: stringToArrayValue(ai.family_members),
  };
}

function buildOverrides(form: OverrideFormState, initial: OverrideFormState): SubjectOverrides {
  const overrides: SubjectOverrides = {};
  const additional_info: AdditionalInfo = {};

  if (form.subject_name !== initial.subject_name && form.subject_name.trim()) {
    overrides.subject_name = form.subject_name.trim();
  }
  if (form.subject_type !== initial.subject_type) {
    overrides.subject_type = form.subject_type || null;
  }
  if (form.reference !== initial.reference) {
    overrides.reference = form.reference || null;
  }
  if (!arraysEqual(form.jurisdiction_codes, initial.jurisdiction_codes)) {
    overrides.jurisdiction_codes = form.jurisdiction_codes;
  }

  const setIfChangedString = (key: keyof AdditionalInfo, current: string, original: string) => {
    if (current !== original) {
      (additional_info as any)[key] = current || null;
    }
  };
  const setIfChangedArray = (key: keyof AdditionalInfo, current: string[], original: string[]) => {
    if (!arraysEqual(current, original)) {
      (additional_info as any)[key] = current;
    }
  };

  setIfChangedString('date_of_birth', form.date_of_birth, initial.date_of_birth);
  setIfChangedString('registration_number', form.registration_number, initial.registration_number);
  setIfChangedString('imo_number', form.imo_number, initial.imo_number);
  setIfChangedString('nationality', form.nationality, initial.nationality);
  setIfChangedArray('aliases', form.aliases, initial.aliases);
  setIfChangedArray('addresses', form.addresses, initial.addresses);
  setIfChangedArray('urls', form.urls, initial.urls);
  setIfChangedString('employer', form.employer, initial.employer);
  setIfChangedArray('previous_employers', form.previous_employers, initial.previous_employers);
  setIfChangedArray('previous_locations', form.previous_locations, initial.previous_locations);
  setIfChangedString('spouse', form.spouse, initial.spouse);
  setIfChangedArray('family_members', form.family_members, initial.family_members);

  if (Object.keys(additional_info).length > 0) {
    overrides.additional_info = additional_info;
  }

  return overrides;
}

export function CandidateRow({
  candidate,
  subjectName,
  subjectType,
  reference,
  jurisdictionCodes,
  additionalInfo,
  disabled = false,
  onSelect,
}: Readonly<CandidateRowProps>) {
  const [editMode, setEditMode] = useState(false);
  const [sourcesExpanded, setSourcesExpanded] = useState(false);

  const initialForm = buildInitialForm(
    subjectName,
    subjectType,
    reference,
    jurisdictionCodes,
    additionalInfo,
    candidate.attributes
  );
  const [form, setForm] = useState<OverrideFormState>(initialForm);

  const attributesEntries = Object.entries(candidate.attributes).filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );

  const visibleSources =
    sourcesExpanded || candidate.sources.length <= COLLAPSE_SOURCES_OVER
      ? candidate.sources
      : candidate.sources.slice(0, COLLAPSE_SOURCES_OVER);

  const handleSelect = () => {
    if (!editMode) {
      onSelect(candidate.id);
      return;
    }
    const overrides = buildOverrides(form, initialForm);
    onSelect(candidate.id, Object.keys(overrides).length > 0 ? overrides : undefined);
  };

  const updateField = <K extends keyof OverrideFormState>(key: K, value: OverrideFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Card
      withBorder
      className={classes.card}
      data-selected={candidate.is_selected || undefined}
      data-confidence={candidate.confidence}
    >
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ flex: 1 }}>
            <Group gap="xs" wrap="wrap">
              <Title order={5} className={classes.name}>
                {candidate.name}
              </Title>
              <Badge color={confidenceColor(candidate.confidence)} variant="light" tt="capitalize">
                {candidate.confidence} confidence
              </Badge>
            </Group>
            {candidate.aliases.length > 0 && (
              <Text size="xs" c="dimmed">
                Also known as: {candidate.aliases.join(', ')}
              </Text>
            )}
          </Stack>
        </Group>

        {attributesEntries.length > 0 && (
          <div className={classes.attributes}>
            {attributesEntries.map(([key, value]) => (
              <div key={key} className={classes.attributeRow}>
                <Text size="xs" c="dimmed" className={classes.attributeKey}>
                  {prettifyKey(key)}
                </Text>
                <Text size="sm">{renderAttributeValue(value)}</Text>
              </div>
            ))}
          </div>
        )}

        {candidate.sources.length > 0 && (
          <Stack gap={4}>
            <Text size="xs" fw={500} c="dimmed">
              Sources
            </Text>
            {visibleSources.map((src) => (
              <div key={src.url} className={classes.source}>
                <Anchor href={src.url} target="_blank" rel="noopener noreferrer" size="xs">
                  {src.url}
                </Anchor>
                {src.snippet && (
                  <Text size="xs" c="dimmed" className={classes.snippet}>
                    {src.snippet}
                  </Text>
                )}
              </div>
            ))}
            {candidate.sources.length > COLLAPSE_SOURCES_OVER && (
              <Button
                variant="subtle"
                size="compact-xs"
                onClick={() => setSourcesExpanded((v) => !v)}
                leftSection={
                  sourcesExpanded ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />
                }
              >
                {sourcesExpanded ? 'Show fewer' : `Show all ${candidate.sources.length} sources`}
              </Button>
            )}
          </Stack>
        )}

        <Collapse in={editMode}>
          <Stack gap="xs" className={classes.editPanel}>
            <Divider label="Correct subject details" labelPosition="left" />
            <TextInput
              label="Subject name"
              value={form.subject_name}
              onChange={(e) => updateField('subject_name', e.currentTarget.value)}
              size="xs"
            />
            <Select
              label="Subject type"
              value={form.subject_type || null}
              onChange={(v) => updateField('subject_type', v ?? '')}
              data={SUBJECT_TYPE_OPTIONS}
              clearable
              size="xs"
            />
            <TextInput
              label="Reference"
              value={form.reference}
              onChange={(e) => updateField('reference', e.currentTarget.value)}
              size="xs"
            />
            {isVesselSubjectType(form.subject_type) && (
              <TextInput
                label="IMO number"
                value={form.imo_number}
                onChange={(e) => updateField('imo_number', e.currentTarget.value)}
                placeholder="e.g. 9116412"
                size="xs"
              />
            )}
            <TagsInput
              label={`${jurisdictionFieldLabel(form.subject_type, false)} codes (ISO alpha-2)`}
              value={form.jurisdiction_codes}
              onChange={(v) => updateField('jurisdiction_codes', v)}
              placeholder="e.g. DE, GB"
              size="xs"
            />
            <Divider label="Additional info" labelPosition="left" />
            <Group grow>
              <TextInput
                label="Employer"
                value={form.employer}
                onChange={(e) => updateField('employer', e.currentTarget.value)}
                size="xs"
              />
              <TextInput
                label="Date of birth"
                value={form.date_of_birth}
                onChange={(e) => updateField('date_of_birth', e.currentTarget.value)}
                placeholder="YYYY-MM-DD"
                size="xs"
              />
            </Group>
            <Group grow>
              <TextInput
                label="Nationality"
                value={form.nationality}
                onChange={(e) => updateField('nationality', e.currentTarget.value)}
                size="xs"
              />
              <TextInput
                label="Registration number"
                value={form.registration_number}
                onChange={(e) => updateField('registration_number', e.currentTarget.value)}
                size="xs"
              />
            </Group>
            <TextInput
              label="Spouse"
              value={form.spouse}
              onChange={(e) => updateField('spouse', e.currentTarget.value)}
              size="xs"
            />
            <TagsInput
              label="Aliases"
              value={form.aliases}
              onChange={(v) => updateField('aliases', v)}
              size="xs"
            />
            <TagsInput
              label="Addresses"
              value={form.addresses}
              onChange={(v) => updateField('addresses', v)}
              size="xs"
            />
            <TagsInput
              label="URLs"
              value={form.urls}
              onChange={(v) => updateField('urls', v)}
              size="xs"
            />
            <TagsInput
              label="Previous employers"
              value={form.previous_employers}
              onChange={(v) => updateField('previous_employers', v)}
              size="xs"
            />
            <TagsInput
              label="Previous locations"
              value={form.previous_locations}
              onChange={(v) => updateField('previous_locations', v)}
              size="xs"
            />
            <TagsInput
              label="Family members"
              value={form.family_members}
              onChange={(v) => updateField('family_members', v)}
              size="xs"
            />
          </Stack>
        </Collapse>

        <Group justify="flex-end" gap="xs">
          <Button
            variant="subtle"
            size="xs"
            onClick={() => setEditMode((v) => !v)}
            leftSection={<IconEdit size={14} />}
            disabled={disabled}
          >
            {editMode ? 'Hide edits' : 'Edit attributes'}
          </Button>
          <Button
            size="xs"
            onClick={handleSelect}
            disabled={disabled}
            color={confidenceColor(candidate.confidence)}
          >
            {editMode ? 'Select with edits' : 'Select'}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
